<template>
  <form ref="form" class="needs-validation" @submit.prevent="save">
    <div class="row">
      <div v-if="title" class="col-sm-12 my-3">
        <div class="order-source-title">
          <div>
            <span class="order-source-kicker">{{ sourceKicker }}</span>
            <h3 class="mb-1">{{ sourceTitle }}</h3>
            <small class="text-muted">{{ sourceDescription }}</small>
          </div>
          <span class="order-source-badge">
            <i :class="stockSource === STOCK_SOURCE_DISTRIBUTOR ? 'fas fa-boxes' : 'fas fa-warehouse'"></i>
            {{ sourceLabel }}
          </span>
        </div>
      </div>

      <div v-if="serverErrors.length" class="col-12 mb-3">
        <div class="stock-error" role="alert">
          <i class="fas fa-exclamation-circle"></i>
          <div>
            <strong>No se pudo agregar el artículo</strong>
            <div v-for="(error, index) in serverErrors" :key="index">{{ error }}</div>
          </div>
        </div>
      </div>

      <div class="col-sm-3">
        <div class="form-group">
          <label class="control-label"> Artículo </label>

          <el-select
            v-validate="modelValidations.article"
            :value="selectedArticleText"
            name="article"
            autocomplete="off"
            placeholder="Artículo"
            filterable
            @change="setArticle"
          >
            <el-option v-for="option in articlesOptions" :key="option.value" :label="option.label" :value="option">
              <div class="article-option">
                <span>{{ option.label }}</span>
                <span>({{ quantityOption(option) }})</span>
              </div>
            </el-option>
          </el-select>

          <span v-if="formData.article">
            <small v-if="formData.article.availability > 0" class="text-muted">
              Cantidad disponible: {{ formData.article.availability }}
            </small>
            <small v-else class="text-danger"> Cantidad disponible: {{ formData.article.availability }} </small>
          </span>
          <small v-show="article.invalid" class="text-danger">
            {{ getError('article') }}
          </small>
        </div>
      </div>

      <div class="col-sm-2">
        <div class="form-group">
          <label class="control-label"> Cantidad </label>

          <el-input
            v-model="formData.quantity"
            v-validate="modelValidations.quantity"
            name="quantity"
            pattern="[0-9]+"
            placeholder="Cantidad"
            suffix-icon="fas fa-boxes"
            type="number"
            :disabled="!(formData.article && formData.article.availability > 0)"
          >
          </el-input>

          <small v-if="checkStock" class="text-danger"> No hay suficientes articulos disponibles </small>

          <small v-if="checkCero" class="text-danger"> La cantidad no puede ser cero </small>

          <small v-show="quantity.invalid" class="text-danger">
            {{ getError('quantity') }}
          </small>
        </div>
      </div>

      <div class="col-sm-2">
        <div class="form-group">
          <label class="control-label text-nowrap"> Precio Unitario (USD) </label>

          <el-input
            v-model="formData.price"
            v-validate="modelValidations.price"
            name="price"
            pattern="[0-9]+([\.,][0-9]+)?"
            placeholder="Precio Unitario"
            step="0.01"
            suffix-icon="fas fa-dollar-sign"
            type="number"
          >
          </el-input>

          <small v-show="price.invalid" class="text-danger">
            {{ getError('price') }}
          </small>
        </div>
      </div>
      <div class="col-sm-2">
        <div class="form-group">
          <label class="control-label text-nowrap">Costo de distribuidor (USD)</label>

          <el-input
            v-model="formData.distributorCost"
            v-validate="modelValidations.distributorCost"
            name="distributorCost"
            placeholder="Costo de distribuidor"
            suffix-icon="fas fa-dollar-sign"
          />

          <small v-show="distributorCost.invalid" class="text-danger">
            {{ getError('distributorCost') }}
          </small>
        </div>
      </div>
      <div class="col-sm-2 pt-4 mx-4">
        <base-button
          class="rounded-pill mt-2"
          native-type="submit"
          size="sm"
          type="success"
          :disabled="checkStock || checkCero"
        >
          <i class="fas fa-plus-circle mr-1"></i><span>Agregar</span>
        </base-button>
      </div>
    </div>
    <div v-if="!hasArticles" class="row">
      <div class="col">
        <p class="font-italic text-danger">Asegúrese de tener artículos cargados para la variedad asociada</p>
      </div>
    </div>
  </form>
</template>

<script>
import { Select, Option } from 'element-ui';
import { mapFields } from 'vee-validate';

export default {
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
  },

  props: {
    title: {
      type: String,
      default: 'Agregar Artículos',
    },

    order: {
      type: Number,
      required: true,
    },

    campaign: {
      type: Object,
      required: true,
    },

    projection: {
      type: Object,
      default: null,
    },

    stockSource: {
      type: String,
      default: 'central',
    },

    distributorName: {
      type: String,
      default: 'el distribuidor asignado',
    },
  },

  data() {
    return {
      articles: [],
      validated: false,
      serverErrors: [],
      STOCK_SOURCE_DISTRIBUTOR: 'distributor',

      formData: {
        article: null,
        quantity: null,
        price: null,
        distributorCost: null,
      },

      modelValidations: {
        article: {
          required: true,
        },

        quantity: {
          required: true,
          numeric: true,
        },

        price: {
          required: true,
          decimal: 2,
        },

        distributorCost: {
          required: true,
          decimal: 2,
          min_value: 0.01,
        },
      },
    };
  },

  computed: {
    ...mapFields(['article', 'quantity', 'price', 'distributorCost']),

    articlesOptions() {
      return this.$selectOptions(this.articles, { sort: true, label: 'description' });
    },

    sourceLabel() {
      return this.stockSource === this.STOCK_SOURCE_DISTRIBUTOR ? 'MiDepósito' : 'Casa Central';
    },

    sourceKicker() {
      return this.stockSource === this.STOCK_SOURCE_DISTRIBUTOR ? 'STOCK DEL DISTRIBUIDOR' : 'DISPONIBILIDAD GENERAL';
    },

    sourceTitle() {
      return this.stockSource === this.STOCK_SOURCE_DISTRIBUTOR
        ? 'Agregar artículos desde MiDepósito'
        : 'Agregar artículos desde Casa Central';
    },

    sourceDescription() {
      return this.stockSource === this.STOCK_SOURCE_DISTRIBUTOR
        ? `Stock disponible por artículo del distribuidor ${this.distributorName}.`
        : 'Disponibilidad actual de Casa Central.';
    },

    selectedArticleText() {
      const article = this.formData.article;

      return article ? `${article.description} (${article.availability})` : null;
    },

    hasArticles() {
      return this.articles.length > 0;
    },

    quantityOption() {
      return (option) => {
        const article = this.articles.find((article) => article.id === option.value);

        return article ? article.availability : '';
      };
    },

    checkCero() {
      return this.formData.article ? this.formData.quantity == 0 : false;
    },

    checkStock() {
      return this.formData.article ? this.formData.quantity > this.formData.article.availability : false;
    },
  },

  mounted() {
    this.refreshAvailability();
    this.formData.quantity = this.projection ? this.projection.quantity : null;
  },

  methods: {
    setArticle(article) {
      this.formData.article = this.articles.find(({ id }) => id === article.value);
    },

    refreshAvailability() {
      this.serverErrors = [];
      this.$services
        .resource('orders')
        .availableArticles(this.order)
        .then((res) => res.data.data)
        .then((data) => {
          this.articles = data.articles.map((article) => ({
            ...article,
            availability: Number(article.orderAvailability),
          }));
        })
        .catch((errors) => {
          this.serverErrors = this.normalizeErrors(errors);
        });
    },

    async firstFormSubmit() {
      try {
        return await this.$validator.validateAll();
      } finally {
        this.validated = true;
      }
    },

    save() {
      this.firstFormSubmit().then((valid) => {
        if (valid) {
          let newItem = {
            article: this.formData.article,
            quantity: +this.formData.quantity,
            price: +this.formData.price,
            distributorCost: this.formData.distributorCost,
          };

          this.$services
            .resource('order_items')
            .setOrder(this.order)
            .create(newItem)
            .then((res) => {
              newItem = res.data.data;
              this.$emit('addItem', newItem);
            })
            .catch((errors) => {
              const messages = this.normalizeErrors(errors);
              this.refreshAvailability();
              this.serverErrors = messages;
            });
        }
      });
    },

    normalizeErrors(errors) {
      if (Array.isArray(errors)) {
        return errors;
      }
      if (errors && errors.response && errors.response.data && errors.response.data.message) {
        return [errors.response.data.message];
      }
      return ['La disponibilidad cambió. Actualizá los datos e intentá nuevamente.'];
    },

    clean() {
      this.formData.article = null;
      this.formData.quantity = null;
      this.formData.price = null;
      this.formData.distributorCost = null;
      this.validated = false;
      this.serverErrors = [];
      this.errors.clear();
      this.$validator.reset();
    },

    reset() {
      this.$refs.form.reset();
      this.clean();
    },

    getError(name) {
      return this.errors.first(name);
    },

    isValid(name) {
      return this.validated && !this.errors.has(name);
    },
  },
};
</script>

<style scoped>
::v-deep .article-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.order-source-title {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.9rem;
  border-bottom: 2px solid #172b4d;
}

.order-source-kicker {
  display: block;
  margin-bottom: 0.2rem;
  color: #8898aa;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.order-source-badge {
  padding: 0.42rem 0.7rem;
  border: 1px solid #f4c430;
  border-radius: 0.3rem;
  color: #172b4d;
  font-size: 0.76rem;
  font-weight: 700;
  white-space: nowrap;
  background: #fff8d6;
}

.order-source-badge i {
  margin-right: 0.35rem;
}

.stock-error {
  display: flex;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border: 1px solid #f5c6cb;
  border-left: 4px solid #f5365c;
  border-radius: 0.35rem;
  color: #7f1d1d;
  font-size: 0.82rem;
  background: #fff5f5;
}

.stock-error > i {
  margin-top: 0.15rem;
  color: #f5365c;
}

@media (max-width: 767px) {
  .order-source-title {
    display: block;
  }

  .order-source-badge {
    display: inline-block;
    margin-top: 0.75rem;
  }
}
</style>
